import React from 'react';
import { useParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import Pagination from '@mui/material/Pagination';
import { useFeature } from 'flagged';
import useContributionDialog from '@/components/common/contribution/useContributionDialog';
import DonationsDataTable from '@/components/common/DonationsDataTable';
import Loader from '@/components/common/Loader';
import PageSizeSelect from '@/components/common/PageSizeSelect';
import FeatureFlags from '@/constants/system/FeatureFlags';
import useConstituent from '@/lib/hooks/swr/useConstituent';
import useDonationListView from '@/lib/hooks/swr/useDonationListView';
import translate from '@/lib/intl/translate';
import { useEntityDetails } from '@/modules/EntityDetails';
import {
  ConstituentOpportunitiesFilterParams,
  OpportunitiesSortableFields,
} from '@/modules/Opportunities';
import QueryProvider, { useQueryProvider } from '@/modules/QueryProvider';
import ConstituentOpportunitiesDataList from './ConstituentOpportunitiesDataList';
import ConstituentRecurringDonationsContainer from './ConstituentRecurringDonationsContainer';
import OutstandingPledgesContainer from './OutstandingPledgesContainer';

function ConstituentContributionsTab() {
  const hasOpportunityMilestone2 = useFeature(
    FeatureFlags.OPPORTUNITIES_MILESTONE_2,
  );
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const { constituentId } = useParams();
  const { data: constituent } = useConstituent(constituentId);

  const { openDonationEntity } = useEntityDetails();

  const { query, updateQuery } = useQueryProvider();
  const { page } = query;

  const { data } = useDonationListView({
    ...query,
    constituentIds: constituentId,
    dateType: null,
    startDate: null,
  });

  const closeContributionDialog = React.useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const openContributionDialog = React.useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  useContributionDialog(isDialogOpen, {
    onClose: closeContributionDialog,
    preSelectedDonorId: constituentId,
  });

  const onPageChange = (e, value) => updateQuery({ page: value });
  const onPageSizeChange = (e) => updateQuery({ pageSize: e.target.value });

  const handleViewClick = React.useCallback((donation) => {
    openDonationEntity(constituentId, donation.donationId);
  }, []);

  const totalPages = Math.ceil((data?.meta.count || 0) / query.pageSize);

  const hasContributions = !!data?.report.length;

  return (
    <div className="grid gap-11">
      <ConstituentRecurringDonationsContainer
        data={constituent?.donationPlans}
      />
      <OutstandingPledgesContainer
        constituentId={constituentId}
        onViewClick={handleViewClick}
      />
      {hasOpportunityMilestone2 && (
        <QueryProvider
          filterParams={ConstituentOpportunitiesFilterParams}
          sortableFields={OpportunitiesSortableFields}
          filterPrefix="opp"
          sortingPrefix="opp"
        >
          <ConstituentOpportunitiesDataList />
        </QueryProvider>
      )}
      <div className="grid gap-4">
        <div className="flex items-center">
          <h2 className="type-h3">{`${translate('CONTRIBUTIONS')} (${
            data?.meta.count ?? '-'
          })`}</h2>
          {hasContributions && (
            <PageSizeSelect
              value={query.pageSize}
              className="ml-auto"
              onChange={onPageSizeChange}
            />
          )}
        </div>
        {!data?.report && <Loader isCentered className="my-4" />}
        {!!data?.report && hasContributions && (
          <>
            <DonationsDataTable data={data.report} showConstituent={false} />

            <div className="mt-6 flex flex-col items-center">
              {!!data?.report.length && (
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={onPageChange}
                  shape="rounded"
                />
              )}
            </div>
          </>
        )}
        {!!data?.report && !hasContributions && (
          <div>
            <p>{translate('NO_CONTRIBUTIONS_TO_DISPLAY')}</p>
            <Button onClick={openContributionDialog} className="px-0">
              {translate('ADD_NEW_CONTRIBUTION')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ConstituentContributionsTab;
